// Settings → Trash: everything deleted (and voided invoices) for 30 days — restore it, or delete it for good.
import { useMemo, useState } from 'react';
import { api, useApi, type Row } from '../lib';
import { Button, Loading, ErrorBox, Empty, useToast } from '../ui';

const GROUPS: [string, string][] = [
  ['all', 'All'], ['invoices', 'Invoices (void)'], ['work_orders', 'Work orders / estimates'], ['jobs', 'Jobs & lines'], ['parts', 'Parts'],
  ['photos', 'Photos'], ['documents', 'Documents'], ['money', 'Payments & expenses'], ['other', 'Other'],
];
const daysLeft = (iso: string) => Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 86400_000));
const when = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function TrashPanel() {
  const { data, error, reload } = useApi<Row[]>('/api/trash');
  const toast = useToast();
  const [g, setG] = useState('all');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string[]>([]);
  const [busy, setBusy] = useState('');
  const counts = useMemo(() => {
    const m: Record<string, number> = { all: data?.length || 0 };
    for (const x of data || []) m[x.group] = (m[x.group] || 0) + 1;
    return m;
  }, [data]);
  const list = useMemo(() => (data || []).filter(x => (g === 'all' || x.group === g) && (!q.trim() || `${x.label} ${x.detail} ${x.deleted_by_name || ''}`.toLowerCase().includes(q.trim().toLowerCase()))), [data, g, q]);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const allOn = list.length > 0 && list.every(x => sel.includes(x.id));
  const run = async (key: string, fn: () => Promise<void>) => { setBusy(key); try { await fn(); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(''); } };
  const restoreOne = (id: string) => run(id, async () => { await api(`/api/trash/${id}/restore`, { body: {} }); toast('Restored'); setSel(s => s.filter(x => x !== id)); reload(); });
  const restoreSel = () => run('restore', async () => {
    const r = await api<Row>('/api/trash/restore', { body: { ids: sel } });
    toast(`${r.restored} restored${r.failed.length ? ` — not restored: ${r.failed.join('; ')}` : ''}`, r.failed.length ? 'err' : undefined); setSel([]); reload();
  });
  const deleteSel = () => {
    if (!confirm(`Delete ${sel.length} item${sel.length === 1 ? '' : 's'} for good? This can't be undone.`)) return;
    void run('delete', async () => { const r = await api<Row>('/api/trash/delete', { body: { ids: sel } }); toast(`${r.deleted} deleted for good`); setSel([]); reload(); });
  };
  return (
    <div className="trash">
      <p className="muted">Deleted things stay here for 30 days, then they are gone for good. A voided invoice comes back with the same number.</p>
      <div className="chip-row trash-groups">{GROUPS.map(([id, label]) => <button key={id} type="button" className={'chip' + (g === id ? ' on' : '')} onClick={() => { setG(id); setSel([]); }}>
        {label}{counts[id] ? ` (${counts[id]})` : ''}</button>)}</div>
      <div className="trash-tools">
        <input className="mini-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search number, name…" aria-label="Search the trash" />
        <span className="spacer" />
        {sel.length > 0 && <>
          <Button busy={busy === 'restore'} onClick={() => void restoreSel()}>Restore {sel.length}</Button>
          <Button kind="danger" busy={busy === 'delete'} onClick={deleteSel}>Delete forever</Button>
        </>}
      </div>
      {!list.length ? <Empty>{data.length ? 'Nothing deleted in this group.' : 'The trash is empty. Deleted work orders, lines, payments, photos and voided invoices show up here for 30 days.'}</Empty> :
        <div className="table-wrap"><table className="trash-table">
          <thead><tr><th><input type="checkbox" checked={allOn} aria-label="Select all" onChange={e => setSel(e.target.checked ? list.map(x => x.id) : [])} /></th><th>What</th><th>Deleted</th><th>Gone in</th><th /></tr></thead>
          <tbody>{list.map(x => <tr key={x.id} className={sel.includes(x.id) ? 'on' : ''}>
            <td><input type="checkbox" checked={sel.includes(x.id)} aria-label={`Select ${x.label}`} onChange={e => setSel(s => e.target.checked ? [...s, x.id] : s.filter(y => y !== x.id))} /></td>
            <td><b>{x.label}</b>{x.detail && <small className="muted trash-sub">{x.detail}</small>}</td>
            <td><span className="nowrap">{when(x.deleted_at)}</span><small className="muted trash-sub">{x.deleted_by_name || "—"}</small></td>
            <td className="nowrap">{daysLeft(x.purge_on)} days</td>
            <td><Button className="small" busy={busy === x.id} onClick={() => void restoreOne(x.id)}>Restore</Button></td>
          </tr>)}</tbody>
        </table></div>}
    </div>
  );
}
